// Lib
import React, { useCallback } from 'react';
import { compose } from '../../../../node_module_clones/recompose';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// Utils
import shouldAutoEmbed, { platformAllowsIFrameEmbeds } from '../utils/shouldAutoEmbed';
import {
    getImageProp,
    getLinkUrl,
    getMedia,
    getLinkMediaType,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getPlatformTypeSelector } from '../../../platform/platformSelector';
import { sanitiseUrl } from '../../../../common/utils/urlUtil';

// Components
import richMediaConnectDecorator from '../richMediaConnectDecorator';
import RichMediaPreviewImage from './RichMediaPreviewImage';
import RichMediaContent from './RichMediaContent';
import RichMediaDragPreview from './RichMediaDragPreview';

import previewImageDropTargetDecorator from '../../dnd/elementDropTargets/previewImageDropTargetDecorator';

const enhance = compose(richMediaConnectDecorator, previewImageDropTargetDecorator());

const openElementLink = (element) => {
    const sanitizedUrl = sanitiseUrl(getLinkUrl(element));
    if (!sanitizedUrl) return;

    window.open(sanitizedUrl, '_blank');
};

const RichMedia = (props) => {
    const {
        element,
        className,
        showPreview,
        showRichMedia,
        embeddingRichMedia,
        isPresentational,
        ContentComponent = RichMediaContent,
        PreviewComponent,
        connectFileDropTarget,
        attachment,
        dispatchRichMediaEmbedding,
    } = props;

    const platformType = useSelector(getPlatformTypeSelector);

    const mediaData = getMedia(element);
    const imageData = getImageProp(element);
    const linkMediaType = getLinkMediaType(element);
    const showOverlayPlayIcon = platformAllowsIFrameEmbeds(platformType)
        ? showPreview
        : showPreview && linkMediaType === 'player';

    const onPreviewClick = useCallback(
        (event) => {
            if (!platformAllowsIFrameEmbeds(platformType)) {
                event.preventDefault();
                event.stopPropagation();
                return openElementLink(element);
            }

            event.stopPropagation();
            dispatchRichMediaEmbedding();
        },
        [dispatchRichMediaEmbedding],
    );

    let RenderedPreviewComponent = PreviewComponent;
    if (!RenderedPreviewComponent) {
        RenderedPreviewComponent = shouldAutoEmbed(element, platformType)
            ? RichMediaDragPreview
            : RichMediaPreviewImage;
    }

    const renderContent = (
        <div className={classNames('RichMedia', className)}>
            <ContentComponent {...props} show={showRichMedia && !isPresentational} mediaData={mediaData} />
            <RenderedPreviewComponent
                {...props}
                show={showPreview || isPresentational}
                showLoader={embeddingRichMedia}
                showPreview={showOverlayPlayIcon}
                mediaData={mediaData}
                imageData={imageData}
                onClick={onPreviewClick}
                attachment={attachment}
            />
        </div>
    );

    return connectFileDropTarget ? connectFileDropTarget(renderContent) : renderContent;
};

RichMedia.propTypes = {
    width: PropTypes.number,
    gridSize: PropTypes.number,
    tempMediaSize: PropTypes.object,
    element: PropTypes.object.isRequired,
    PreviewComponent: PropTypes.elementType,
    ContentComponent: PropTypes.elementType,
    showPreview: PropTypes.bool,
    embeddingRichMedia: PropTypes.bool,
    showRichMedia: PropTypes.bool,
    dispatchRichMediaEmbedding: PropTypes.func,
    className: PropTypes.string,
    isPresentational: PropTypes.bool,
    attachment: PropTypes.object,
    connectFileDropTarget: PropTypes.func,
};

export default enhance(RichMedia);
